'use client'

import React, { useEffect, useMemo, useRef } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { AdditiveBlending, ShaderMaterial, Vector2, Vector3 } from 'three'
import { PASSTHROUGH_VERT } from '@/components/FinishingMove/shaders/passthrough.vert'
import { ATTACK_PROJECTILE_V2_FRAG } from './attackProjectileV2.frag'

export type AttackProjectileType = 'physical' | 'fire' | 'ice' | 'lightning' | 'arcane' | 'earth'

// Type → shader branch index (must match attackProjectile.frag).
export const PROJECTILE_TYPE_INDEX: Record<AttackProjectileType, number> = {
  physical: 0,
  fire: 1,
  ice: 2,
  lightning: 3,
  arcane: 4,
  earth: 5,
}

// Default tints matched to the hit-indicator / lab attack colours.
export const PROJECTILE_TINT: Record<AttackProjectileType, [number, number, number]> = {
  physical: [1.0, 0.42, 0.37],
  fire: [1.0, 0.32, 0.14],
  ice: [0.45, 0.85, 1.0],
  lightning: [1.0, 0.9, 0.45],
  arcane: [0.74, 0.55, 1.0],
  earth: [0.78, 0.45, 0.26],
}

export interface SceneProps {
  fromUv: [number, number]
  toUv: [number, number]
  type: AttackProjectileType
  tint?: [number, number, number]
  durationMs: number
  // Override the fragment source (same uniform contract). Used by /vfx-lab to
  // preview candidate shaders; the game always uses the default.
  frag?: string
}

// Exported so the shared persistent ProjectileLayer can render N of these in a
// single Canvas (one WebGL context) instead of mounting a Canvas per attack.
export const ProjectileScene: React.FC<SceneProps> = ({ fromUv, toUv, type, tint, durationMs, frag }) => {
  const matRef = useRef<ShaderMaterial>(null)
  const startRef = useRef(performance.now() / 1000)
  const size = useThree((s) => s.size)

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uDuration: { value: durationMs / 1000 },
      uFrom: { value: new Vector2(fromUv[0], fromUv[1]) },
      uTo: { value: new Vector2(toUv[0], toUv[1]) },
      uAspect: { value: new Vector2(1, 1) },
      uTint: { value: new Vector3(...(tint ?? PROJECTILE_TINT[type])) },
      uType: { value: PROJECTILE_TYPE_INDEX[type] },
    }),
    // Frozen at mount — a re-render mustn't snap the projectile mid-flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  useFrame(() => {
    const m = matRef.current
    if (!m) return
    m.uniforms.uTime.value = performance.now() / 1000 - startRef.current
    const aspect = size.width / size.height
    m.uniforms.uAspect.value.set(aspect >= 1 ? aspect : 1, aspect >= 1 ? 1 : 1 / aspect)
  })

  return (
    <mesh frustumCulled={false}>
      <planeGeometry args={[2, 2]} />
      <shaderMaterial
        ref={matRef}
        vertexShader={PASSTHROUGH_VERT}
        fragmentShader={frag ?? ATTACK_PROJECTILE_V2_FRAG}
        uniforms={uniforms}
        transparent
        depthTest={false}
        depthWrite={false}
        blending={AdditiveBlending}
      />
    </mesh>
  )
}

export interface AttackProjectileProps extends SceneProps {
  onDone?: () => void
}

/**
 * Self-contained directional attack projectile overlay (own full-screen
 * canvas, like the finisher scenes). Renders a projectile travelling from
 * `fromUv` → `toUv` (UV space, origin bottom-left) for `durationMs`, then
 * calls `onDone`. Mount it with a fresh `key` to replay.
 */
const AttackProjectile: React.FC<AttackProjectileProps> = ({ onDone, durationMs, ...scene }) => {
  // Keep onDone in a ref so the dismiss timer depends ONLY on durationMs. The
  // parent passes an inline arrow that changes identity on every re-render
  // (e.g. the lab's 500ms fps overlay); without this the timer would reset
  // each render and the projectile would never self-dismiss.
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone
  useEffect(() => {
    const id = window.setTimeout(() => onDoneRef.current?.(), durationMs + 120)
    return () => window.clearTimeout(id)
  }, [durationMs])

  return (
    <Canvas
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      camera={{ position: [0, 0, 1], fov: 50 }}
      dpr={[1, 1.5]}
      gl={{ alpha: true, antialias: false, powerPreference: 'high-performance' }}
    >
      <ProjectileScene durationMs={durationMs} {...scene} />
    </Canvas>
  )
}

export default AttackProjectile
